import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Footer from "src/components/wizard/Footer/Footer";
import useAuth from "src/hooks/useAuth";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import Header from "../../../wizard/Header";
import WeightMismatchAlert from "../WeightMismatchAlert";
import getTotalReceivingUnitsLabel from "../Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import InventoryRequestPickerModal from "../../../modals/InventoryRequestPickerModal";
import useReceivingWizardFlow from "../hooks/useReceivingWizardFlow";
import {
  ADD_INVENTORY_ITEM_MUTATION,
  CREATE_INBOUND_RESERVATION_MUTATION,
  UPDATE_SHIPMENT_MUTATION,
} from "src/constants/graphql/mutations";
import { loadReservationPlan, saveReservationPlan } from "./storage";
import { applyInboundReservation, buildAppliedReservationPlan } from "./applyInboundReservation";
import ReservationItemSyncStatus from "./ReservationItemSyncStatus";
import {
  getReceivableShipmentItems,
  getReservationItemSync,
  mergeAppliedShipmentItems,
} from "./reservationItemSync";
import Empty from "./Empty";

const GENERAL_INVENTORY_NOTE = "Items received for general inventory.";

function stripReservationOutcomeNote(notes = "") {
  return notes
    .replace(/\n\nItems received for general inventory\.?$/, "")
    .replace(/\n\nItems added to Reservation #[\s\S]*$/, "")
    .trimEnd();
}

function buildReservationOutcomeNote(plan, inventoryRequest) {
  if (!plan) {
    return null;
  }

  if (plan.type === "skip") {
    return GENERAL_INVENTORY_NOTE;
  }

  const workOrderNumber = inventoryRequest?.workOrderNumber || plan.workOrderNumber;
  if (!workOrderNumber) {
    return null;
  }

  const comment = inventoryRequest?.comment || plan.comment || plan.inventoryRequestComment;

  return comment
    ? `Items added to Reservation #${workOrderNumber} for ${comment}`
    : `Items added to Reservation #${workOrderNumber}`;
}

function mergeReservationOutcomeNote(notes, plan, inventoryRequest) {
  const reservationOutcomeNote = buildReservationOutcomeNote(plan, inventoryRequest);
  if (!reservationOutcomeNote) {
    return notes;
  }

  const baseNotes = stripReservationOutcomeNote(notes);
  return baseNotes ? `${baseNotes}\n\n${reservationOutcomeNote}` : reservationOutcomeNote;
}

function ReservationActionModal({ open, onClose, onSkip, onAdd, canAddToReservation }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontWeight={500} fontSize={18} color="text.primary" align="center">
          Reservation Options
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={1}>
          <Button variant="outlined" onClick={onAdd} disabled={!canAddToReservation}>
            Add to Reservation
          </Button>
          <Divider sx={{ my: 1 }} />
          <Button
            variant="outlined"
            onClick={onSkip}
            sx={{
              borderColor: "grey.400",
              color: "grey.700",
              "&:hover": {
                borderColor: "grey.600",
                backgroundColor: "grey.50",
              },
            }}
          >
            Receive as General Inventory
          </Button>
        </Box>
        {!canAddToReservation && (
          <Typography mt={2} color="warning.main">
            No received shipment quantities are available to add to a reservation.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateReservationModal({ open, onClose, onSubmit }) {
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setComment("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Reservation</DialogTitle>
      <DialogContent>
        <Typography mb={2} color="text.secondary">
          A new reservation will be created and received items will be added immediately.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Reservation Notes"
          value={comment}
          onChange={event => setComment(event.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSubmit(comment.trim())} disabled={!comment.trim()}>
          Save Reservation
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReservationSummary({
  plan,
  receivedItems,
  onChangeSelection,
  onAddRemaining,
  addingRemaining,
}) {
  if (!plan) {
    return null;
  }

  if (plan.type === "skip") {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={6} px={2}>
        <Typography variant="h6" color="success.main" align="center">
          Reservation Step Skipped
        </Typography>
        <Typography mt={2} maxWidth={520} align="center" color="text.secondary">
          This shipment will continue without adding the received items to a reservation.
        </Typography>
        <Button sx={{ mt: 3 }} variant="outlined" onClick={onChangeSelection}>
          Add to Reservation Instead
        </Button>
      </Box>
    );
  }

  if (plan.type === "existing") {
    const hasAppliedItems = (plan.appliedShipmentItems || []).some(
      entry => Number(entry.quantity) > 0
    );

    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={6} px={2}>
        {plan.applied ? (
          <ReservationItemSyncStatus
            plan={plan}
            receivedItems={receivedItems}
            workOrderNumber={plan.workOrderNumber}
            onAddRemaining={onAddRemaining}
            addingRemaining={addingRemaining}
          />
        ) : (
          <Typography mt={2} maxWidth={520} align="center" color="text.secondary">
            These received shipment items are ready to be added to Reservation #
            {plan.workOrderNumber}.
          </Typography>
        )}
        <Button sx={{ mt: 3 }} variant="outlined" onClick={onChangeSelection}>
          Change Reservation
        </Button>
      </Box>
    );
  }

  const newReservationMessage = plan.applied
    ? "A new reservation was created and received items were added using the notes below."
    : "A new reservation will be created and the received items will be added using the notes below.";

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={6} px={2}>
      <Typography variant="h6" align="center">
        {plan.applied ? "New Reservation Created" : "New Reservation Ready"}
      </Typography>
      {!plan.applied && (
        <Typography mt={2} maxWidth={520} align="center" color="text.secondary">
          {newReservationMessage}
        </Typography>
      )}
      {plan.applied && (
        <ReservationItemSyncStatus
          plan={plan}
          receivedItems={receivedItems}
          workOrderNumber={plan.workOrderNumber}
          onAddRemaining={onAddRemaining}
          addingRemaining={addingRemaining}
        />
      )}
      <Box
        mt={3}
        p={2}
        width="100%"
        maxWidth={520}
        border="1px solid"
        borderColor="divider"
        borderRadius={2}
        bgcolor="#f9fafb"
      >
        <Typography whiteSpace="pre-wrap">{plan.comment}</Typography>
      </Box>
      <Button sx={{ mt: 3 }} variant="outlined" onClick={onChangeSelection}>
        Edit Reservation Notes
      </Button>
    </Box>
  );
}

export default function Reservations({ shipment, project, verticalLayout = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyError, notifySuccess } = useNotification();
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION);
  const [createInboundReservation] = useMutation(CREATE_INBOUND_RESERVATION_MUTATION);
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const totalPieces = shipment?.totalPieces || [];
  const { hasAdditionalCharges, reservationsStepIndex, totalSteps } =
    useReceivingWizardFlow(project);
  const [plan, setPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const receivedItems = useMemo(
    () => getReceivableShipmentItems(shipment),
    [shipment?.shipmentItems]
  );
  const reservationSync = useMemo(
    () => getReservationItemSync(receivedItems, plan?.appliedShipmentItems || []),
    [receivedItems, plan?.appliedShipmentItems]
  );

  useEffect(() => {
    setPlan(loadReservationPlan(shipment.id));
  }, [shipment.id]);

  const persistPlan = nextPlan => {
    setPlan(nextPlan);
    saveReservationPlan(shipment.id, nextPlan);
  };

  const persistReservationOutcomeNote = async (nextPlan, inventoryRequest = null) => {
    const notes = mergeReservationOutcomeNote(shipment.notes, nextPlan, inventoryRequest);

    if (notes === shipment.notes) {
      return true;
    }

    let saveError = null;
    await updateShipment({
      variables: {
        id: shipment.id,
        input: {
          notes,
        },
      },
      onFailure: errors => {
        saveError = errors?.submit || errors?.base || Object.values(errors || {})[0];
      },
    });

    if (saveError) {
      notifyError("We couldn't save the reservation summary on this shipment.");
      return false;
    }

    return true;
  };

  const handleBack = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", hasAdditionalCharges ? "additional-charges" : "items");
    navigate({ pathname, search: params.toString() });
  };

  const handleNext = () => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", "os&d");
    navigate({ pathname, search: params.toString() });
  };

  const handleChooseSkip = async () => {
    const nextPlan = { type: "skip" };
    persistPlan(nextPlan);
    setOpenActionModal(false);
    await persistReservationOutcomeNote(nextPlan);
    handleNext();
  };

  const applyPlan = async (draftPlan, pendingEntries = null) => {
    setProcessing(true);

    const entries =
      pendingEntries ||
      receivedItems.map(shipmentItem => ({
        shipmentItem,
        quantityToAdd: Number(shipmentItem.quantity),
      }));

    const sameReservation =
      plan?.applied && plan.inventoryRequestId === draftPlan.inventoryRequestId;
    const baseSnapshot = sameReservation ? plan.appliedShipmentItems || [] : [];

    try {
      const result = await applyInboundReservation({
        addInventoryItem,
        createInboundReservation,
        project,
        plan: draftPlan,
        shipmentItems: entries,
      });

      if (result.inventoryRequest?.id) {
        const appliedShipmentItems = mergeAppliedShipmentItems(
          baseSnapshot,
          (result.appliedEntries || []).map(({ shipmentItem, quantityToAdd }) => ({
            shipmentItemId: shipmentItem.id,
            quantity: Number(quantityToAdd),
            assetId: shipmentItem.asset.id,
          }))
        );

        const appliedPlan = buildAppliedReservationPlan(
          draftPlan,
          result.inventoryRequest,
          appliedShipmentItems
        );
        persistPlan(appliedPlan);
      }

      if (!result.success) {
        notifyError(result.error);
        return false;
      }

      await persistReservationOutcomeNote(
        buildAppliedReservationPlan(
          draftPlan,
          result.inventoryRequest,
          mergeAppliedShipmentItems(
            baseSnapshot,
            (result.appliedEntries || []).map(({ shipmentItem, quantityToAdd }) => ({
              shipmentItemId: shipmentItem.id,
              quantity: Number(quantityToAdd),
              assetId: shipmentItem.asset.id,
            }))
          )
        ),
        result.inventoryRequest
      );

      notifySuccess(
        `Received items added to Reservation #${result.inventoryRequest.workOrderNumber}`
      );
      return true;
    } finally {
      setProcessing(false);
    }
  };

  const handleAddRemaining = async () => {
    if (!plan?.inventoryRequestId || reservationSync.pending.length === 0) {
      return;
    }

    await applyPlan(
      {
        type: "existing",
        inventoryRequestId: plan.inventoryRequestId,
        workOrderNumber: plan.workOrderNumber,
        inventoryRequestComment: plan.inventoryRequestComment,
        comment: plan.comment,
      },
      reservationSync.pending
    );
  };

  const handleChooseExistingReservation = async reservation => {
    const draftPlan = {
      type: "existing",
      inventoryRequestId: reservation.id,
      workOrderNumber: reservation.workOrderNumber,
      inventoryRequestComment: reservation.comment,
    };

    if (plan?.applied && plan.inventoryRequestId !== reservation.id) {
      const confirmed = window.confirm(
        `Items were already added to Reservation #${plan.workOrderNumber}. ` +
          "Selecting a different reservation will add the same received items there as well. Continue?"
      );
      if (!confirmed) {
        return;
      }
    }

    const applied = await applyPlan(draftPlan);
    if (!applied) {
      return;
    }

    setOpenPicker(false);
    setOpenActionModal(false);
  };

  const handleCreateReservation = async comment => {
    const applied = await applyPlan({
      type: "new",
      comment,
    });

    if (!applied) {
      return;
    }

    setOpenCreateModal(false);
    setOpenActionModal(false);
  };

  const canAddToReservation = receivedItems.length > 0;
  const projectLabel = (user.company?.settings?.filter_field2 || "Project").toLowerCase();

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
      <Header title="Add to Reservation" info={getTotalReceivingUnitsLabel(totalPieces)} />
      <WeightMismatchAlert shipment={shipment} />
      <Box p={2} flexGrow={1}>
        {processing && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}
        {!processing && plan ? (
          <ReservationSummary
            plan={plan}
            receivedItems={receivedItems}
            onChangeSelection={() => setOpenActionModal(true)}
            onAddRemaining={handleAddRemaining}
            addingRemaining={processing}
          />
        ) : !processing ? (
          <Box display="flex" flexDirection="column" alignItems="center" mt={6} px={2}>
            <Empty projectLabel={projectLabel} />
            <Box my={4}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenActionModal(true)}
              >
                Review Reservation Options
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box mt={2}>
        <Footer
          step={reservationsStepIndex}
          steps={totalSteps}
          startTime={verticalLayout ? null : shipment?.startTime}
          completeTime={verticalLayout ? null : shipment?.completeTime}
        >
          <div style={{ width: verticalLayout ? "100%" : "auto" }}>
            {!verticalLayout && (
              <Button onClick={handleBack} variant="outlined" sx={{ mr: 1, textTransform: "none" }}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              fullWidth={verticalLayout}
              sx={{ textTransform: "none" }}
              disabled={
                processing ||
                !plan ||
                (plan.type !== "skip" && (!plan.applied || !reservationSync.allSynced))
              }
              onClick={handleNext}
            >
              {verticalLayout ? "Next Step" : "Next"}
            </Button>
          </div>
        </Footer>
      </Box>
      {openActionModal && (
        <ReservationActionModal
          open={openActionModal}
          onClose={() => setOpenActionModal(false)}
          onSkip={handleChooseSkip}
          onAdd={() => {
            setOpenActionModal(false);
            setOpenPicker(true);
          }}
          canAddToReservation={canAddToReservation}
        />
      )}
      {openPicker && (
        <InventoryRequestPickerModal
          type="reservations"
          open={openPicker}
          onClose={() => setOpenPicker(false)}
          onSelect={handleChooseExistingReservation}
          actionLabel="Select Reservation"
          emptyActionLabel="Create New Reservation"
          onEmptyAction={() => {
            setOpenPicker(false);
            setOpenCreateModal(true);
          }}
          queryVariables={{
            projectId: Number(project.id),
            sortBy: { field: "created_at", sortDirection: "desc" },
          }}
        />
      )}
      {openCreateModal && (
        <CreateReservationModal
          open={openCreateModal}
          onClose={() => setOpenCreateModal(false)}
          onSubmit={handleCreateReservation}
        />
      )}
    </Box>
  );
}
